import { z } from 'zod';

/**
 * Webapp register schema. Mirrors the hand-rolled validators
 * previously in src/app/router/actions/helpers.ts + registerAction.ts:
 *   - firstName / lastName: required, >= 2 chars after trim
 *   - email: required + RFC-ish regex
 *   - password: required + >= 8 chars + must contain upper, lower,
 *     digit and special character (the strong rule webapp enforces
 *     server-side)
 *   - phoneNumber: optional, but if present must match phone regex
 *   - accountType: 'individual' | 'organization'
 *   - organization: required when accountType === 'organization'
 */
export const registerSchema = z
  .object({
    firstName: z
      .string()
      .min(1, 'First name is required')
      .refine((v) => v.trim().length >= 2, {
        message: 'First name must be at least 2 characters',
      }),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .refine((v) => v.trim().length >= 2, {
        message: 'Last name must be at least 2 characters',
      }),
    email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
    password: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters')
      .regex(/(?=.*[a-z])(?=.*[A-Z])/, {
        message: 'Password must contain uppercase and lowercase letters',
      })
      .regex(/\d/, {
        message: 'Password must contain at least one number',
      })
      .regex(/[^a-zA-Z\d]/, {
        message: 'Password must contain at least one special character',
      }),
    phoneNumber: z.string().optional().default(''),
    accountType: z.enum(['individual', 'organization']),
    organization: z.string().optional().default(''),
  })
  .superRefine((data, ctx) => {
    if (data.phoneNumber && !/^\+?[\d\s\-()]+$/.test(data.phoneNumber)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phoneNumber'],
        message: 'Please enter a valid phone number',
      });
    }
    if (data.accountType === 'organization' && !data.organization.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['organization'],
        message: 'Organization name is required',
      });
    }
  });

export type RegisterInput = z.input<typeof registerSchema>;
export type RegisterOutput = z.output<typeof registerSchema>;
